import { ARTICLE_CONTENT_WRAPPER_WIDTH_ATTRIBUTE } from "@components/Layouts/CatalogLayout/ArticleLayout/consts";
import { useArticleWidthStyle } from "@components/Layouts/CatalogLayout/ArticleLayout/useArticleDimensions";
import {
	ARTICLE_POPOVER_PADDING,
	getArticlePopoverBoundary,
	getArticlePopoverContainer,
} from "@ext/markdown/core/edit/logic/articlePopover";
import InlineEditPanel, {
	type InlineToolbarButtons,
} from "@ext/markdown/elements/article/edit/helpers/InlineEditPanel";
import { updatePlacement } from "@ext/markdown/elements/article/edit/logic/resolveInlineToolbarPlacement";
import { CustomBubbleMenu } from "@ext/markdown/elements/customBubbleMenu/edit/components/CustomBubbleMenu";
import type { Editor } from "@tiptap/react";
import { CellSelection, isInTable } from "prosemirror-tables";
import { memo, type RefObject, useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { Instance, Props } from "tippy.js";
import { getBlockToolbarRegistry } from "./BlockToolbarRegistry";
import { getToolbarReferenceRect } from "./getToolbarReferenceRect";
import { resolveToolbarContext, type ToolbarContext } from "./resolveToolbarContext";

const INLINE_TOOLBAR_Z_INDEX = 49;

interface InlineToolbarProps {
	editor: Editor;
	shouldShow: (props: { editor: Editor }) => boolean;
	pluginKey?: string;
	buttons?: InlineToolbarButtons;
	boundaryRef?: RefObject<HTMLElement>;
}

export interface InlineToolbarOptions {
	isInTable: boolean;
	isCellSelection: boolean;
}

export const InlineToolbar = memo(({ editor, pluginKey, buttons, shouldShow, boundaryRef }: InlineToolbarProps) => {
	const articleWidthStyle = useArticleWidthStyle();
	const [options, setOptions] = useState<(InlineToolbarOptions & { toolbarContext: ToolbarContext }) | null>(null);
	const tippyInstanceRef = useRef<Instance<Props>>(null);

	const onShow = useCallback(
		(instance: Instance<Props>) => {
			tippyInstanceRef.current = instance;
			if (editor && !editor.isDestroyed) updatePlacement(instance, resolveToolbarContext(editor.state));
			requestAnimationFrame(() => {
				if (instance?.popperInstance) {
					instance.popperInstance.update();
				}
			});
		},
		[editor],
	);

	const closeHandler = useCallback(() => {
		if (tippyInstanceRef.current) {
			tippyInstanceRef.current.hide();
		}
	}, []);

	const onHide = useCallback(() => {
		tippyInstanceRef.current = null;
		if (!editor || editor.isDestroyed) return;
		if (resolveToolbarContext(editor.state).kind === "text") editor.commands.focus();
	}, [editor]);

	const onHidden = useCallback(() => {
		setOptions(null);
	}, []);

	const handleShouldShow = useCallback(
		(props: Parameters<typeof shouldShow>[0]) => {
			if (!props.editor || props.editor.isDestroyed) return false;
			const show = shouldShow(props);
			if (!show) return false;
			const toolbarContext = resolveToolbarContext(props.editor.state);

			flushSync(() => {
				setOptions({
					isInTable: isInTable(props.editor.state),
					isCellSelection: props.editor.state.selection instanceof CellSelection,
					toolbarContext,
				});
			});
			updatePlacement(tippyInstanceRef.current, toolbarContext);
			return true;
		},
		[shouldShow],
	);

	const refreshOnBlockToolbar = useCallback(
		(refresh: () => void) => getBlockToolbarRegistry(editor).subscribe(refresh),
		[editor],
	);

	const appendTo = useCallback(() => {
		return getArticlePopoverContainer(editor, boundaryRef?.current);
	}, [boundaryRef, editor]);

	const getTippyOptions = useCallback((): Partial<Props> => {
		const boundary = getArticlePopoverBoundary(editor, boundaryRef?.current, "viewport");

		return {
			maxWidth: "unset",
			getReferenceClientRect: () => getToolbarReferenceRect(editor),
			appendTo,
			interactive: true,
			arrow: false,
			sticky: true,
			offset: [0, 8],
			zIndex: INLINE_TOOLBAR_Z_INDEX,
			placement: "top-start",
			duration: [220, 200],
			animation: "article-popover",
			moveTransition: "transform 0.150s ease-in-out",
			onShow,
			onHide,
			onHidden,
			popperOptions: {
				modifiers: [
					{
						name: "preventOverflow",
						options: { boundary, padding: ARTICLE_POPOVER_PADDING },
					},
				],
			},
		};
	}, [appendTo, boundaryRef, editor, onHide, onHidden, onShow]);

	return (
		<CustomBubbleMenu
			editor={editor}
			pluginKey={pluginKey || "inline-toolbar"}
			refreshOn={refreshOnBlockToolbar}
			shouldShow={handleShouldShow}
			tippyOptions={getTippyOptions}
		>
			<div
				className="article-popover article-popover-stagger rounded-lg sm:[&>div]:rounded-full"
				style={{
					...articleWidthStyle,
					maxWidth: `var(${ARTICLE_CONTENT_WRAPPER_WIDTH_ATTRIBUTE})`,
				}}
			>
				{options && (
					<InlineEditPanel
						buttons={buttons}
						className="shadow-glass-xl"
						closeHandler={closeHandler}
						editor={editor}
						{...options}
					/>
				)}
			</div>
		</CustomBubbleMenu>
	);
});
